import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type {
  ActiveReloadView,
  AppListQuery,
  AppListResponse,
  ConnectionCreateBody,
  ConnectionTestBody,
  ConnectionTestResult,
  ConnectionUpdateBody,
  ConnectionView,
  ImmediateReloadBody,
  ReloadHistoryQuery,
  ReloadHistoryView,
  ScheduleCreateBody,
  ScheduleView,
  AuditListResponse,
  AuditQuery,
  SpacesResponse,
  BulkReloadBody,
  BulkReloadResponse,
  BulkScheduleBody,
  BulkScheduleResponse,
  QlikReloadsResponse,
} from "@qlik-scheduler/shared";

import { apiRequest } from "./client.js";

// --- Connections -----------------------------------------------------------

export const connectionsKeys = {
  all: ["connections"] as const,
  list: () => [...connectionsKeys.all, "list"] as const,
  single: (id: number) => [...connectionsKeys.all, "single", id] as const,
};

export function useConnections(
  options?: Omit<UseQueryOptions<ConnectionView[]>, "queryKey" | "queryFn">,
) {
  return useQuery<ConnectionView[]>({
    queryKey: connectionsKeys.list(),
    queryFn: ({ signal }) => apiRequest<ConnectionView[]>("/api/connections", { signal }),
    ...options,
  });
}

export function useConnection(
  id: number | null,
  options?: Omit<UseQueryOptions<ConnectionView>, "queryKey" | "queryFn" | "enabled">,
) {
  return useQuery<ConnectionView>({
    queryKey: id != null ? connectionsKeys.single(id) : ["connections", "single", "disabled"],
    queryFn: ({ signal }) =>
      apiRequest<ConnectionView>(`/api/connections/${id}`, { signal }),
    enabled: id != null,
    ...options,
  });
}

export function useCreateConnection() {
  const qc = useQueryClient();
  return useMutation<ConnectionView, Error, ConnectionCreateBody>({
    mutationFn: (body) =>
      apiRequest<ConnectionView>("/api/connections", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: connectionsKeys.all });
    },
  });
}

export function useUpdateConnection(id: number) {
  const qc = useQueryClient();
  return useMutation<ConnectionView, Error, ConnectionUpdateBody>({
    mutationFn: (body) =>
      apiRequest<ConnectionView>(`/api/connections/${id}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: connectionsKeys.all });
    },
  });
}

export function useDeleteConnection() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) =>
      apiRequest<void>(`/api/connections/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: connectionsKeys.all });
    },
  });
}

export function useTestConnectionUnsaved() {
  // Pre-save test: full body in, discriminated result out.
  return useMutation<ConnectionTestResult, Error, ConnectionTestBody>({
    mutationFn: (body) =>
      apiRequest<ConnectionTestResult>("/api/connections/test", {
        method: "POST",
        body,
      }),
  });
}

export function useTestConnectionSaved() {
  const qc = useQueryClient();
  return useMutation<ConnectionTestResult, Error, number>({
    mutationFn: (id) =>
      apiRequest<ConnectionTestResult>(`/api/connections/${id}/test`, {
        method: "POST",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: connectionsKeys.all });
    },
  });
}

// --- Apps ------------------------------------------------------------------

// Subset of AppListQuery that drives a query. We accept the un-validated shape
// from the UI and stringify into the URL ourselves.
export type AppsQueryArgs = {
  connectionId: number;
  search?: string;
  spaceId?: string;
  canReloadOnly?: boolean;
  cursor?: string;
  limit?: number;
};

export const appsKeys = {
  all: ["apps"] as const,
  list: (args: AppsQueryArgs) => [...appsKeys.all, "list", args] as const,
};

function buildAppsUrl(args: AppsQueryArgs): string {
  const params = new URLSearchParams();
  params.set("connectionId", String(args.connectionId));
  if (args.search) params.set("search", args.search);
  if (args.spaceId) params.set("spaceId", args.spaceId);
  if (args.canReloadOnly) params.set("canReloadOnly", "true");
  if (args.cursor) params.set("cursor", args.cursor);
  if (args.limit != null) params.set("limit", String(args.limit));
  return `/api/apps?${params.toString()}`;
}

export function useApps(
  args: AppsQueryArgs,
  options?: Omit<UseQueryOptions<AppListResponse>, "queryKey" | "queryFn">,
) {
  return useQuery<AppListResponse>({
    queryKey: appsKeys.list(args),
    queryFn: ({ signal }) =>
      apiRequest<AppListResponse>(buildAppsUrl(args), { signal }),
    ...options,
  });
}

// Provided for callers (Apps tab) that want to type-check the query arg type.
export type AppListQueryType = AppListQuery;

// --- Schedules -------------------------------------------------------------

export const schedulesKeys = {
  all: ["schedules"] as const,
  list: () => [...schedulesKeys.all, "list"] as const,
};

export function useSchedules(
  options?: Omit<UseQueryOptions<ScheduleView[]>, "queryKey" | "queryFn">,
) {
  return useQuery<ScheduleView[]>({
    queryKey: schedulesKeys.list(),
    queryFn: ({ signal }) => apiRequest<ScheduleView[]>("/api/schedules", { signal }),
    ...options,
  });
}

export function useCreateSchedule() {
  const qc = useQueryClient();
  return useMutation<ScheduleView, Error, ScheduleCreateBody>({
    mutationFn: (body) =>
      apiRequest<ScheduleView>("/api/schedules", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: schedulesKeys.all });
    },
  });
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) =>
      apiRequest<void>(`/api/schedules/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: schedulesKeys.all });
    },
  });
}

export function useRunScheduleNow() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) =>
      apiRequest<void>(`/api/schedules/${id}/run-now`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: schedulesKeys.all });
    },
  });
}

export function useResumeSchedule() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) =>
      apiRequest<void>(`/api/schedules/${id}/resume`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: schedulesKeys.all });
    },
  });
}

// --- Reloads (immediate + active + history) --------------------------------

export const reloadsKeys = {
  all: ["reloads"] as const,
  active: () => [...reloadsKeys.all, "active"] as const,
  history: (args: ReloadHistoryArgs) => [...reloadsKeys.all, "history", args] as const,
};

export function useImmediateReload() {
  const qc = useQueryClient();
  return useMutation<
    { reloadId: string; qlikReloadId: string; status: string },
    Error,
    ImmediateReloadBody
  >({
    mutationFn: (body) =>
      apiRequest("/api/reloads/immediate", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reloadsKeys.active() });
    },
  });
}

export function useActiveReloads(
  options?: Omit<UseQueryOptions<ActiveReloadView[]>, "queryKey" | "queryFn">,
) {
  return useQuery<ActiveReloadView[]>({
    queryKey: reloadsKeys.active(),
    queryFn: ({ signal }) =>
      apiRequest<ActiveReloadView[]>("/api/reloads/active", { signal }),
    refetchInterval: 5000,
    ...options,
  });
}

export function useCancelReload() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (reloadId) =>
      apiRequest<void>(`/api/reloads/${reloadId}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reloadsKeys.active() });
    },
  });
}

export type ReloadHistoryArgs = Partial<ReloadHistoryQuery> & {
  // Same shape as ReloadHistoryQuery but every field optional from the UI side;
  // server applies its own defaults.
};

export type ReloadHistoryResponse = {
  data: ReloadHistoryView[];
  total: number;
};

function buildHistoryUrl(args: ReloadHistoryArgs): string {
  const params = new URLSearchParams();
  if (args.appId) params.set("appId", args.appId);
  if (args.status) params.set("status", args.status);
  if (args.operationId) params.set("operationId", args.operationId);
  if (args.from) params.set("from", args.from);
  if (args.to) params.set("to", args.to);
  if (args.limit != null) params.set("limit", String(args.limit));
  if (args.offset != null) params.set("offset", String(args.offset));
  const qs = params.toString();
  return qs ? `/api/reloads/history?${qs}` : "/api/reloads/history";
}

export function useReloadHistory(
  args: ReloadHistoryArgs,
  options?: Omit<UseQueryOptions<ReloadHistoryResponse>, "queryKey" | "queryFn">,
) {
  return useQuery<ReloadHistoryResponse>({
    queryKey: reloadsKeys.history(args),
    queryFn: ({ signal }) =>
      apiRequest<ReloadHistoryResponse>(buildHistoryUrl(args), { signal }),
    ...options,
  });
}


// --- Audit log -------------------------------------------------------------

export type AuditQueryArgs = {
  action?: string;
  targetType?: string;
  targetId?: string;
  actorUserId?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
};

export const auditKeys = {
  all: ["audit"] as const,
  list: (args: AuditQueryArgs) => [...auditKeys.all, "list", args] as const,
};

function buildAuditUrl(args: AuditQueryArgs): string {
  const params = new URLSearchParams();
  if (args.action) params.set("action", args.action);
  if (args.targetType) params.set("targetType", args.targetType);
  if (args.targetId) params.set("targetId", args.targetId);
  if (args.actorUserId) params.set("actorUserId", args.actorUserId);
  if (args.from) params.set("from", args.from);
  if (args.to) params.set("to", args.to);
  if (args.limit != null) params.set("limit", String(args.limit));
  if (args.offset != null) params.set("offset", String(args.offset));
  const qs = params.toString();
  return qs ? `/api/audit?${qs}` : "/api/audit";
}

export function useAuditLog(
  args: AuditQueryArgs,
  options?: Omit<UseQueryOptions<AuditListResponse>, "queryKey" | "queryFn">,
) {
  return useQuery<AuditListResponse>({
    queryKey: auditKeys.list(args),
    queryFn: ({ signal }) =>
      apiRequest<AuditListResponse>(buildAuditUrl(args), { signal }),
    ...options,
  });
}

// Suppress unused-import warning when no caller references AuditQuery directly.
export type AuditQueryType = AuditQuery;

// --- Spaces (per connection) -----------------------------------------------

export const spacesKeys = {
  all: ["spaces"] as const,
  byConnection: (connectionId: number) =>
    [...spacesKeys.all, connectionId] as const,
};

export function useSpaces(
  connectionId: number | null,
  options?: Omit<UseQueryOptions<SpacesResponse>, "queryKey" | "queryFn" | "enabled">,
) {
  return useQuery<SpacesResponse>({
    queryKey:
      connectionId != null
        ? spacesKeys.byConnection(connectionId)
        : ["spaces", "disabled"],
    queryFn: ({ signal }) =>
      apiRequest<SpacesResponse>(`/api/connections/${connectionId}/spaces`, {
        signal,
      }),
    enabled: connectionId != null,
    // Names rarely change; 10-min cache.
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

// --- Bulk operations -------------------------------------------------------

export function useBulkReload() {
  const qc = useQueryClient();
  return useMutation<BulkReloadResponse, Error, BulkReloadBody>({
    mutationFn: (body) =>
      apiRequest<BulkReloadResponse>("/api/reloads/bulk", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reloadsKeys.active() });
    },
  });
}

export function useBulkSchedule() {
  const qc = useQueryClient();
  return useMutation<BulkScheduleResponse, Error, BulkScheduleBody>({
    mutationFn: (body) =>
      apiRequest<BulkScheduleResponse>("/api/schedules/bulk", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: schedulesKeys.all });
    },
  });
}

// --- Qlik-tenant reload feed (external + ours) -----------------------------

export const qlikReloadsKeys = {
  all: ["qlik-reloads"] as const,
  list: (key: string) => [...qlikReloadsKeys.all, key] as const,
};

export function useQlikReloads(
  connectionIds: number[],
  scope: "active" | "all",
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  const ids = [...connectionIds].sort().join(",");
  return useQuery<QlikReloadsResponse>({
    queryKey: qlikReloadsKeys.list(`${scope}:${ids}`),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({
        connectionIds: ids,
        scope,
        limit: "100",
      });
      return apiRequest<QlikReloadsResponse>(
        `/api/qlik-reloads?${params.toString()}`,
        { signal },
      );
    },
    enabled: (options?.enabled ?? true) && connectionIds.length > 0,
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 4_000,
  });
}
